import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useNotifications, useUnreadCount, useMarkAsRead, useMarkAllAsRead } from '@/services/notifications/hooks';
import { getResolvedJoinRequest } from '@/services/notifications/types';
import { useAuthStore } from '@/stores/auth-store';

/** Notifications shown in the dropdown before it asks the reader to go elsewhere. */
const DROPDOWN_LIMIT = 5;

interface NotificationBellProps {
  /**
   * Where "View all notifications" leads. Omit to hide that footer entirely —
   * the admin shell has no notifications page of its own, and sending an admin
   * to the learner one would drop them out of the admin layout mid-task.
   */
  viewAllTo?: string;
  /**
   * Render the unread count in the badge instead of a bare dot. The admin shell
   * turns this on: an admin's notifications are a work queue, so "how many" is
   * the whole question. Learners only need to know there is something new.
   */
  showCount?: boolean;
}

/** Format ISO date string to relative time for display */
function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

/**
 * Outcome badge for a join-request notification that has already been decided.
 * Keeps the row legible as history — the reader can tell at a glance that it is
 * a record, not a task, without re-reading the message.
 */
function OutcomePill({ outcome }: { outcome: 'approved' | 'rejected' }) {
  const approved = outcome === 'approved';
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[0.625rem] font-semibold uppercase tracking-wide ${
        approved
          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
          : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
      }`}
    >
      {approved ? 'Approved' : 'Declined'}
    </span>
  );
}

/**
 * Notification bell + dropdown, shared by the learner header (AppHeader) and the
 * admin header (AdminHeader).
 *
 * Shared rather than duplicated because both shells want the same behaviour on the
 * same rows: the backend writes every notification against a user id, so an admin's
 * join-request notifications and a learner's course notifications come off one
 * endpoint. Only the framing differs, which is what the two props cover.
 *
 * The list is fetched only while the dropdown is open; the unread count polls on its
 * own (60s, set in the hook) so the badge stays live without holding the list in memory.
 *
 * @author Tutara Team
 */
export default function NotificationBell({ viewAllTo, showCount = false }: NotificationBellProps) {
  const navigate = useNavigate();
  const { isAuthenticated, isAuthReady } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Real notification data from API — only fetch when auth is validated
  const { data: notifications = [] } = useNotifications({
    enabled: isAuthReady && isAuthenticated && isOpen,
  });
  const { data: unreadCount = 0 } = useUnreadCount({
    enabled: isAuthReady && isAuthenticated,
  });
  const { mutate: markRead } = useMarkAsRead();
  const { mutate: markAllRead } = useMarkAllAsRead();

  const hasUnread = unreadCount > 0;
  const displayed = notifications.slice(0, DROPDOWN_LIMIT);

  // Mark notification as read when clicked. A resolved row (a join request that has
  // already been approved or declined) is history: the backend cleared its actionUrl
  // when there is nowhere useful left to go, so the row simply does not navigate.
  const handleNotificationClick = (notificationId: string, isRead: boolean, actionUrl?: string) => {
    if (!isRead) markRead(notificationId);
    if (actionUrl) {
      setIsOpen(false);
      navigate(actionUrl);
    }
  };

  const handleViewAll = () => {
    if (!viewAllTo) return;
    setIsOpen(false);
    navigate(viewAllTo);
  };

  // Click outside handler
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 hover:bg-gray-100 dark:hover:bg-tutara-dark-mode-bg rounded-lg"
        aria-label={hasUnread ? `Notifications (${unreadCount} unread)` : 'Notifications'}
        title="Notifications"
        data-testid="notification-bell"
        data-has-notifications={hasUnread ? 'true' : 'false'}
      >
        <Bell className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        {hasUnread && (
          showCount ? (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[1.15rem] h-[1.15rem] px-1 flex items-center justify-center text-[0.65rem] font-semibold text-white bg-red-500 rounded-full"
              data-testid="notification-bell-count"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          ) : (
            <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full"></span>
          )
        )}
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-80 bg-white dark:bg-tutara-dark-mode-surface rounded-lg shadow-xl border border-gray-200 dark:border-tutara-dark-mode-border"
          data-testid="notification-panel"
        >
          <div className="p-4 border-b border-gray-200 dark:border-tutara-dark-mode-border flex items-center justify-between">
            <h3 className="font-semibold text-tutara-charcoal dark:text-white">Notifications</h3>
            {hasUnread && (
              <button
                onClick={() => markAllRead()}
                className="text-xs text-tutara-primary hover:text-tutara-primary-hover font-medium transition-colors"
              >
                Mark all as read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {displayed.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                No notifications yet
              </div>
            ) : (
              displayed.map((notif) => {
                const resolved = getResolvedJoinRequest(notif);
                const clickable = Boolean(notif.actionUrl);
                return (
                  <div
                    key={notif.id}
                    onClick={() => handleNotificationClick(notif.id, notif.read, notif.actionUrl)}
                    className={`p-4 border-b border-gray-100 dark:border-tutara-dark-mode-border last:border-0 transition-colors ${
                      clickable ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-tutara-dark-mode-bg' : 'cursor-default'
                    } ${!notif.read ? 'bg-blue-100/80 dark:bg-blue-900/30' : ''}`}
                    data-testid={resolved ? `notification-resolved-${resolved.outcome}` : undefined}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`text-sm ${!notif.read ? 'font-semibold' : 'font-medium'} ${
                            resolved ? 'text-tutara-charcoal/70 dark:text-gray-400' : 'text-tutara-charcoal dark:text-white'
                          }`}>
                            {notif.title}
                          </p>
                          {resolved && <OutcomePill outcome={resolved.outcome} />}
                        </div>
                        <p className={`text-sm mt-1 ${
                          resolved ? 'text-gray-500 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'
                        }`}>
                          {notif.message}
                        </p>
                      </div>
                      <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                        {formatRelativeTime(notif.createdAt)}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {viewAllTo && displayed.length > 0 && (
            <div className="p-3 border-t border-gray-200 dark:border-tutara-dark-mode-border">
              <button
                onClick={handleViewAll}
                className="text-sm text-tutara-primary hover:text-tutara-primary-hover font-medium transition-colors"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
